import React, { useEffect, useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../ui/hover-card";
import { BiSolidHelpCircle } from "react-icons/bi";
import { MdFlashOn } from "react-icons/md";

interface InputFieldProps {
  item: any;
  index: number;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: (index: number, value: string) => void;
  placeholder?: string;
  hint?: string;
  warning?: boolean;
  warningMessege?: string;
  disabled?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  item,
  onFocus,
  onBlur,
  index,
  onChange,
  placeholder,
  hint,
  warning,
  warningMessege,
  disabled,
}) => {
  const [value, setValue] = useState(item?.value ?? item?.default_value ?? "");
  const [error, setError] = useState<string | null>(null);
  const ValChange = (val: string) => {
    const min = item?.min_value ?? -Infinity;
    const max = item?.max_value ?? Infinity;
    const allowsNegative = min < 0;

    // Allow empty input
    if (val === "") {
      setValue(val);
      setError(null);
      return;
    }

    // Allow typing "-" if negatives are allowed
    if (val === "-" && allowsNegative) {
      setValue(val);
      setError(null);
      return;
    }

    const numValue = Number(val);

    if (isNaN(numValue) && item?.name?.toLowerCase() !== "lookback") return;

    setValue(val);

    if (
      item?.name?.toLowerCase() !== "lookback" &&
      (numValue < min || numValue > max)
    ) {
      setError(`Value must be between ${min} and ${max}`);
      return;
    }

    setError(null);
    onChange?.(index, val);
  };

  useEffect(() => {
    if (item?.value != value) setValue(item?.value);
  }, [item?.value]);

  const isPeriod = item?.name?.toLowerCase() === "period";
  const isLookback = item?.name?.toLowerCase() === "lookback";

  const isNumberField =
    item?.input_type?.toLowerCase() === "float" ||
    item?.input_type?.toLowerCase() === "int" ||
    item?.input_type?.toLowerCase() === "integer" ||
    item?.input_type?.toLowerCase() === "number" ||
    item?.type?.toLowerCase() === "number" ||
    typeof item?.value === "number" ||
    (!isNaN(Number(item?.value)) && item?.value !== "");

  const showButtons = isPeriod || isNumberField;

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="uppercase text-[10px] font-bold tracking-wider text-muted-foreground flex items-center gap-1">
        {item?.name}
        {hint && (
          <HoverCard openDelay={100}>
            <HoverCardTrigger asChild>
              <button type="button" className="ml-1">
                <BiSolidHelpCircle
                  size={12}
                  className="text-muted-foreground hover:text-foreground"
                />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="text-xs max-w-xs border-border/50 bg-background/90 text-foreground">
              {hint}
            </HoverCardContent>
          </HoverCard>
        )}
      </Label>

      <div className="relative flex items-center bg-background border border-white/20 rounded-lg h-10 overflow-hidden focus-within:ring-1 focus-within:ring-ring transition-colors">
        {showButtons && (
          <button
            type="button"
            className="px-3 h-full text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center font-medium bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => ValChange(String(Number(value || 0) - 1))}
            disabled={disabled}
          >
            -
          </button>
        )}
        <Input
          className="border-none text-center  dark:bg-transparent flex-1 h-full focus-visible:ring-0 p-0 shadow-none rounded-none w-full disabled:opacity-50 disabled:cursor-not-allowed"
          value={value}
          defaultValue={item.default_value}
          max={item.max_value}
          min={item.min_value}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={(e) => ValChange(e.target.value)}
          placeholder={placeholder ?? "0"}
          disabled={disabled}
        />
        {showButtons && (
          <button
            type="button"
            className="px-3 h-full text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center font-medium bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => ValChange(String(Number(value || 0) + 1))}
            disabled={disabled}
          >
            +
          </button>
        )}
        {isLookback && (
          <div className="px-3 text-muted-foreground flex items-center justify-center">
            <MdFlashOn size={16} />
          </div>
        )}
      </div>
      {error && (
        <span
          style={{
            color: "red",
            fontSize: "10px",
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default InputField;
